import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PiEnvelopeDuotone, PiLockDuotone } from 'react-icons/pi';
import { useAuth } from '../app/providers/AuthContext';
import Input from '../shared/common/Input';
import Button from '../shared/common/Button';
import { GOOGLE_CLIENT_ID } from '../config';
import './LoginPage.css';

/* global google */

const IS_DEV_GOOGLE = !GOOGLE_CLIENT_ID || GOOGLE_CLIENT_ID === 'YOUR_GOOGLE_CLIENT_ID_HERE';
const DEV_MOCK_TOKEN = 'dev-mock-google-token';

const LoginPage = () => {
  const { loginWithGoogle, login } = useAuth();

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // В dev-режиме (нет реального Client ID) GSI SDK не инициализируем
    if (IS_DEV_GOOGLE) return;

    const init = () => {
      if (!window.google?.accounts) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleResponse,
      });
      window.google.accounts.id.renderButton(
        document.getElementById('google-signin-button'),
        { theme: 'filled_black', size: 'large', width: 320 }
      );
    };

    if (!window.google) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.onload = init;
      document.head.appendChild(script);
    } else {
      init();
    }
  }, []);

  const handleDevGoogleLogin = async () => {
    const result = await loginWithGoogle(DEV_MOCK_TOKEN);
    if (!result.success) setError('Dev Google login failed');
  };

  const handleGoogleResponse = async (response) => {
    const result = await loginWithGoogle(response.credential);
    if (!result.success) setError('Не удалось войти через Google');
  };

  const handleChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(formData.email, formData.password);
      if (!result.success) setError(result.error || 'Неверный email или пароль');
    } catch (err) {
      setError('Произошла ошибка. Попробуй ещё раз.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bm-auth-page">
      {/* Левая hero-панель */}
      <div className="bm-auth-hero">
        <div className="bm-auth-hero__content">
          <div className="bm-auth-hero__logo">⚡ BetMate</div>
          <h1 className="bm-auth-hero__title">
            Делай ставки<br />с друзьями
          </h1>
          <p className="bm-auth-hero__subtitle">
Создавай комнаты, зови друзей<br />и побеждай в честных пари
          </p>
          <div className="bm-auth-hero__features">
            <div className="bm-auth-hero__feature">🏠 Комнаты друзей</div>
            <div className="bm-auth-hero__feature">⚡ Мгновенные результаты</div>
            <div className="bm-auth-hero__feature">🎯 Честные правила</div>
          </div>
        </div>
      </div>

      {/* Правая форма */}
      <div className="bm-auth-form-panel">
        <div className="bm-auth-form-wrap bm-animate-in">
          <h2 className="bm-auth-form-wrap__title">Добро пожаловать</h2>
          <p className="bm-auth-form-wrap__subtitle">Войди в свой аккаунт</p>

          {error && (
            <div className="bm-auth-error">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="bm-auth-form">
            <Input
              label="Email"
              type="email"
              name="email"
              placeholder="you@example.com"
              value={formData.email}
              onChange={handleChange}
              icon={<PiEnvelopeDuotone size={16} />}
              required
            />
            <Input
              label="Пароль"
              type="password"
              name="password"
              placeholder="••••••••"
              value={formData.password}
              onChange={handleChange}
              icon={<PiLockDuotone size={16} />}
              required
            />
            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={loading}
              style={{ width: '100%' }}
            >
              Войти
            </Button>
          </form>

          <div className="bm-auth-divider">
            <span>или</span>
          </div>

          {/* Google Sign-In */}
          {IS_DEV_GOOGLE ? (
            <button
              type="button"
              className="bm-auth-google-dev"
              onClick={handleDevGoogleLogin}
              title="Dev-режим: реальный Google Client ID не задан в .env"
            >
              🔧 Dev: войти как Google User
            </button>
          ) : (
            <div id="google-signin-button" className="bm-auth-google" />
          )}

          <p className="bm-auth-footer">
            Нет аккаунта?{' '}
            <Link to="/register">Зарегистрироваться</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
